/**
 * ReportRenderPage — headless-browser print route for scheduled HazardAI panel reports.
 *
 * Playwright navigates here with a render token and panel spec in the URL.
 * This page:
 *  1. Bootstraps Redux auth state with the render token (so API calls work)
 *  2. Fetches the resolved panel layout from the backend's public layout endpoint
 *  3. Renders each requested HazardAI panel inside a DashboardEntityProvider
 *  4. Tracks in-flight API requests; sets window.__REPORT_READY__ once all settle
 *
 * There is no app chrome (nav, sidebar, header) — the page is PDF-print-only.
 */

import {Box, CircularProgress, Typography} from '@mui/material';
import axios from 'axios';
import {useEffect, useRef, useState} from 'react';
import {useSearchParams} from 'react-router-dom';

import API, {getMessagesFromApiError} from '../../api/axios';
import {apiBaseUrl} from '../../api/urls';
import {HazardAI} from '../../components/dashboard-panels/HazardAI';
import {HazardAIDailyBreakdown} from '../../components/dashboard-panels/HazardAIDailyBreakdown';
import {HazardAIDectionLog} from '../../components/dashboard-panels/HazardAIDetectionLog';
import {HazardAIMachineInteractions} from '../../components/dashboard-panels/HazardAIMachineInteractions';
import {HazardAIMachineSpeedChart} from '../../components/dashboard-panels/HazardAIMachineSpeedChart';
import {HazardAIMachineUtilizationChart} from '../../components/dashboard-panels/HazardAIMachineUtilizationChart';
import {HazardAISummaryChart} from '../../components/dashboard-panels/HazardAISummaryChart';
import {HazardHeatMapReports} from '../../components/dashboard-panels/HazardHeatmap';
import {DashboardEntityProvider} from '../../components/dashboards/entities/DashboardEntityContext';
import {
  DashboardEntity,
  PanelFilter,
  PanelFilterParams,
} from '../../interfaces/Dashboard';
import reduxActions from '../../redux/actions';
import {store} from '../../redux/store';

// Panel code → component map (HazardAI subset)
const HAZARD_AI_PRINT_COMPONENTS: Record<string, React.ComponentType<any>> = {
  HazardAISummaryChart,
  HazardAIDailyBreakdown,
  HazardAIMachineInteractions,
  HazardAIMachineSpeedChart,
  HazardAIMachineUtilizationChart,
  HazardAIDectionLog,
  HazardAI,
  HazardHeatMapReports,
};

// Minimum quiet period (ms) after all requests complete before we call ready
const READY_DEBOUNCE_MS = 1500;

// ---------------------------------------------------------------------------
// Layout data returned by the backend layout endpoint
// ---------------------------------------------------------------------------
interface LayoutData {
  panels: string[];
  panelParams: Record<string, Record<string, unknown>>;
  date_start: string;
  date_end: string;
  company_id: number;
  timezone: string;
  report_name: string;
}

// ---------------------------------------------------------------------------
// Single rendered panel
// ---------------------------------------------------------------------------
interface PrintPanelProps {
  code: string;
  panelFilterParams: PanelFilterParams;
  index: number;
}

const PrintPanel: React.FC<PrintPanelProps> = ({code, panelFilterParams, index}) => {
  const Component = HAZARD_AI_PRINT_COMPONENTS[code];

  if (!Component) {
    return (
      <Box sx={{p: 2, border: '1px solid #ccc', my: 2}}>
        <Typography color="text.secondary">
          Panel &quot;{code}&quot; is not available in print mode.
        </Typography>
      </Box>
    );
  }

  // Build a minimal DashboardEntity for this panel
  const entity: DashboardEntity = {
    id: `print-${code}-${index}`,
    name: code,
    type: 'panel',
    panel: {code: code as any, data: {}},
    grid: {x: 0, y: 0, w: 12, h: 6},
  };

  // Inject resolved filter params (date range, site, camera, etc.)
  const filter: PanelFilter = {
    autoReload: false,
    refreshInterval: null,
    params: panelFilterParams,
  };

  const filterContext = {
    filter,
    setFilter: () => {
      /* read-only in print mode */
    },
  };

  return (
    <Box
      sx={{
        mb: 4,
        pageBreakInside: 'avoid',
        breakInside: 'avoid',
        minHeight: 400,
        position: 'relative',
      }}
    >
      <DashboardEntityProvider
        value={entity}
        filter={filterContext}
        onChange={() => {
          /* read-only */
        }}
      >
        <Component
          value={{}}
          entities={[entity]}
          onUpdate={() => {}}
        />
      </DashboardEntityProvider>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main page component
// ---------------------------------------------------------------------------
declare global {
  interface Window {
    __REPORT_READY__: boolean;
  }
}

const ReportRenderPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [layoutData, setLayoutData] = useState<LayoutData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inFlightRef = useRef(0);
  const readyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const interceptorsRef = useRef<{request: number; response: number} | null>(null);

  // Params from URL
  const renderToken = searchParams.get('renderToken') ?? '';
  const companyId = Number(searchParams.get('companyId') ?? '0');

  // ---- Bootstrap Redux auth with the render token -------------------------
  useEffect(() => {
    if (!renderToken || !companyId) {
      return;
    }

    // Set the render token as the access token so API interceptors pick it up
    reduxActions.auth.setAuthTokens(store.dispatch, {accessToken: renderToken});
    // Set company ID so the X-Company-ID header is sent
    reduxActions.app.setApp(store.dispatch, {companyId});
  }, [renderToken, companyId]);

  // ---- Register in-flight tracker on the global API instance --------------
  useEffect(() => {
    const markMaybeReady = () => {
      if (readyTimerRef.current) {
        clearTimeout(readyTimerRef.current);
      }
      readyTimerRef.current = setTimeout(() => {
        if (inFlightRef.current === 0) {
          window.__REPORT_READY__ = true;
        }
      }, READY_DEBOUNCE_MS);
    };

    const reqId = API.interceptors.request.use((config) => {
      inFlightRef.current += 1;
      if (readyTimerRef.current) {
        clearTimeout(readyTimerRef.current);
      }
      return config;
    });

    const resId = API.interceptors.response.use(
      (response) => {
        inFlightRef.current = Math.max(0, inFlightRef.current - 1);
        markMaybeReady();
        return response;
      },
      (error) => {
        inFlightRef.current = Math.max(0, inFlightRef.current - 1);
        markMaybeReady();
        return Promise.reject(error);
      }
    );

    interceptorsRef.current = {request: reqId, response: resId};

    return () => {
      API.interceptors.request.eject(reqId);
      API.interceptors.response.eject(resId);
      if (readyTimerRef.current) {
        clearTimeout(readyTimerRef.current);
      }
    };
  }, []);

  // ---- Fetch the panel layout from the backend ----------------------------
  useEffect(() => {
    if (!renderToken) {
      return;
    }

    axios
      .get<{success: boolean; data: LayoutData}>(
        `${apiBaseUrl}/hazard-ai-panel-report/render-layout`,
        {params: {token: renderToken}}
      )
      .then((res) => {
        setLayoutData(res.data.data);
      })
      .catch((err) => {
        const msgs = getMessagesFromApiError(err, 'STRA_rnd001');
        setError(msgs.join(', '));
        // Still mark ready so Playwright doesn't hang
        window.__REPORT_READY__ = true;
      });
  }, [renderToken]);

  // ---- Render -------------------------------------------------------------
  if (error) {
    return (
      <Box sx={{p: 4}}>
        <Typography color="error">
          Failed to load report layout: {error}
        </Typography>
      </Box>
    );
  }

  if (!layoutData) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', p: 8}}>
        <CircularProgress />
      </Box>
    );
  }

  const {panels, panelParams, date_start, date_end, report_name} = layoutData;

  return (
    <Box
      sx={{
        p: 3,
        maxWidth: '100%',
        fontFamily: 'sans-serif',
        '@media print': {
          // Hide browser print chrome noise
          '& .panel-selectors, & button': {display: 'none'},
        },
      }}
    >
      {/* Report header */}
      <Box sx={{mb: 4, borderBottom: '2px solid #1976d2', pb: 2}}>
        <Typography variant="h5" fontWeight="bold">
          {report_name || 'HazardAI Panel Report'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Period: {date_start} — {date_end}
        </Typography>
      </Box>

      {/* Panels */}
      {panels.map((code, index) => {
        const overrides = panelParams?.[code] ?? {};

        const panelFilterParams: PanelFilterParams = {
          date_start,
          date_end,
          ...overrides,
        };

        return (
          <PrintPanel
            key={`${code}-${index}`}
            code={code}
            panelFilterParams={panelFilterParams}
            index={index}
          />
        );
      })}
    </Box>
  );
};

export default ReportRenderPage;
